import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarClock, Settings2, Check, X, Vote, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DEFAULT_ELECTION_AT } from "@/lib/constants";

/** Kalan süreyi gün/saat/dakika/saniye olarak verir. */
function kalanHesapla(hedefMs) {
  const fark = Math.max(0, hedefMs - Date.now());
  const toplamSaniye = Math.floor(fark / 1000);
  return {
    bitti: fark === 0,
    toplamGun: Math.floor(toplamSaniye / 86400),
    gun: Math.floor(toplamSaniye / 86400),
    saat: Math.floor(toplamSaniye / 3600) % 24,
    dakika: Math.floor(toplamSaniye / 60) % 60,
    saniye: toplamSaniye % 60,
  };
}

/**
 * Tek hane: 0-9 şeridi yukarı/aşağı kayar, pencere yalnız bir rakam gösterir.
 *
 * Önce her değişimde yeni bir düğüm takılıp eskisi çıkarılıyordu; saniye
 * animasyonu bir tikten uzun sürdüğü için çıkanlar birikip kutuda onlarca
 * rakam kalıyordu. Kayan şeritte hiç mount/unmount yok, o yüzden birikme de yok.
 */
const RAKAMLAR = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

function Hane({ rakam, acil }) {
  const n = Number(rakam) || 0;
  return (
    <span className="relative inline-block w-[0.62em] h-[1.1em] overflow-hidden align-baseline">
      <motion.span
        className="absolute inset-x-0 top-0 flex flex-col"
        style={{ height: "1000%" }}
        animate={{ y: `${-n * 10}%` }}
        transition={{ type: "spring", stiffness: 300, damping: 30, mass: 0.6 }}
      >
        {RAKAMLAR.map((r) => (
          <span
            key={r}
            className={`h-[10%] flex items-center justify-center tabular-nums ${
              acil ? "text-red-50" : "text-white"
            }`}
          >
            {r}
          </span>
        ))}
      </motion.span>
    </span>
  );
}

function Kutu({ deger, etiket, basamak = 2, acil }) {
  const haneler = String(deger).padStart(basamak, "0").split("");
  return (
    <div className="flex flex-col items-center gap-0.5 sm:gap-1.5 min-w-0 shrink-0">
      <div
        className={`relative rounded-lg sm:rounded-2xl px-2 sm:px-4 py-1 sm:py-3 border backdrop-blur-sm overflow-hidden ${
          acil
            ? "bg-red-500/15 border-red-400/40"
            : "bg-white/10 border-white/15"
        }`}
      >
        {/* üstten aşağı hafif parlaklık — rakamlar cam kutuda duruyormuş hissi */}
        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
        <div className="font-heading font-light tracking-tighter text-[20px] sm:text-[40px] leading-none flex">
          {haneler.map((h, i) => (
            <Hane key={i} rakam={h} acil={acil} />
          ))}
        </div>
      </div>
      <span
        className={`text-[7px] sm:text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.14em] font-semibold ${
          acil ? "text-red-200" : "text-white/55"
        }`}
      >
        {etiket}
      </span>
    </div>
  );
}

/** ISO tarihi <input type="datetime-local"> biçimine çevirir (yerel saat). */
function isoToLocalInput(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CountdownBanner({ targetIso, canWrite = false, onSave }) {
  const hedef = targetIso || DEFAULT_ELECTION_AT;
  const hedefMs = useMemo(() => {
    const t = new Date(hedef).getTime();
    return Number.isNaN(t) ? new Date(DEFAULT_ELECTION_AT).getTime() : t;
  }, [hedef]);

  const [kalan, setKalan] = useState(() => kalanHesapla(hedefMs));
  const [duzenle, setDuzenle] = useState(false);
  const [taslak, setTaslak] = useState("");
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setKalan(kalanHesapla(hedefMs));
    timerRef.current = setInterval(() => setKalan(kalanHesapla(hedefMs)), 1000);
    return () => clearInterval(timerRef.current);
  }, [hedefMs]);

  const acil = !kalan.bitti && kalan.toplamGun < 7;
  // Seçim günü (ya da sonrası) sandık şeridi yeşile döner ve dikkat çeker.
  const secimGunu =
    kalan.bitti || new Date().toDateString() === new Date(hedefMs).toDateString();

  const tarihYazisi = useMemo(() => {
    try {
      return new Date(hedefMs).toLocaleString("tr-TR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }, [hedefMs]);

  const kaydet = async () => {
    if (!taslak) return;
    setKaydediliyor(true);
    try {
      // datetime-local yerel saat verir; sunucuya ofsetli ISO gönderiyoruz.
      const d = new Date(taslak);
      await onSave?.(d.toISOString());
      setDuzenle(false);
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      data-testid="countdown-banner"
      className={`relative overflow-hidden rounded-2xl border ${
        acil
          ? "border-red-500/40 bg-gradient-to-br from-red-950 via-neutral-950 to-neutral-950"
          : "border-neutral-800 bg-gradient-to-br from-neutral-900 via-neutral-950 to-black"
      }`}
    >
      {/* arka plan: yavaşça soluklaşıp parlayan halka */}
      <motion.div
        aria-hidden
        className="absolute -right-16 -top-24 h-64 w-64 rounded-full blur-3xl pointer-events-none"
        style={{ backgroundColor: acil ? "#EF4444" : "#10B981" }}
        animate={{ opacity: acil ? [0.22, 0.4, 0.22] : [0.1, 0.18, 0.1] }}
        transition={{ duration: acil ? 2.2 : 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.16) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative px-3.5 sm:px-6 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-6">
        {/* Sol: başlık */}
        <div className="min-w-0 flex-1">
          <div
            className={`flex items-center gap-1.5 text-[8.5px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] font-semibold ${
              acil ? "text-red-300" : "text-emerald-300/80"
            }`}
          >
            <CalendarClock className="h-3 w-3 shrink-0" />
            {kalan.bitti ? "Seçim günü geldi" : "Seçime kalan süre"}
          </div>

          {duzenle ? (
            <div className="flex items-center gap-2 mt-2">
              <Input
                type="datetime-local"
                value={taslak}
                onChange={(e) => setTaslak(e.target.value)}
                className="h-9 w-[13.5rem] bg-white/10 border-white/20 text-white"
                data-testid="countdown-date-input"
              />
              <Button
                size="icon"
                className="h-9 w-9 bg-white text-neutral-950 hover:bg-neutral-200"
                onClick={kaydet}
                disabled={kaydediliyor}
                data-testid="countdown-date-save"
                aria-label="Kaydet"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => setDuzenle(false)}
                aria-label="Vazgeç"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <h2
                className="font-heading text-[13.5px] sm:text-xl font-semibold tracking-tight text-white truncate"
                data-testid="countdown-target-date"
              >
                {tarihYazisi}
              </h2>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => {
                    setTaslak(isoToLocalInput(hedefMs));
                    setDuzenle(true);
                  }}
                  className="shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Seçim tarihini değiştir"
                  data-testid="countdown-edit-btn"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {!kalan.bitti && (
            <p className="hidden sm:block text-[11px] text-white/40 mt-1">
              {kalan.toplamGun > 0
                ? `${kalan.toplamGun} tam gün kaldı`
                : "Son 24 saat"}
            </p>
          )}
        </div>

        {/* Sağ: sayaç */}
        {kalan.bitti ? (
          <div className="font-heading text-2xl sm:text-3xl font-light text-white tracking-tight">
            Bugün!
          </div>
        ) : (
          <div
            className="flex items-start justify-center sm:justify-start gap-1 sm:gap-3 sm:shrink-0"
            data-testid="countdown-clock"
          >
            <Kutu deger={kalan.gun} etiket="Gün" basamak={String(kalan.gun).length > 2 ? 3 : 2} acil={acil} />
            <Kutu deger={kalan.saat} etiket="Saat" acil={acil} />
            <Kutu deger={kalan.dakika} etiket="Dakika" acil={acil} />
            <Kutu deger={kalan.saniye} etiket="Saniye" acil={acil} />
          </div>
        )}
      </div>

      {/* Sayaçla birleşik şerit: seçim günü gelen/gelmeyen takibi */}
      <Link
        to="/sandik"
        data-testid="sandik-link"
        className={`group relative flex items-center gap-3 px-3.5 sm:px-6 py-2.5 sm:py-3 border-t transition-colors ${
          secimGunu
            ? "border-emerald-400/30 bg-emerald-500/20 hover:bg-emerald-500/30"
            : acil
            ? "border-red-400/20 bg-white/[0.04] hover:bg-white/[0.09]"
            : "border-white/10 bg-white/[0.04] hover:bg-white/[0.09]"
        }`}
      >
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30">
          <Vote className="h-4 w-4" />
          {secimGunu && (
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:gap-2 leading-tight">
          <span className="font-heading font-semibold text-[13.5px] sm:text-[15px] text-white">
            Sandık Takibi
          </span>
          <span className="hidden sm:inline text-white/30">·</span>
          <span className="text-[11.5px] sm:text-sm text-white/65">Oy kullananları işaretle</span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-white/50 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </motion.section>
  );
}
